// @flow
import formatMessage from "format-message";

import languages from "./languages";
import { IllegalStateException } from "../exceptions";

import { getSetting } from "../constants";

import type { LocaleConfiguration, MessageParameters } from "./types";

/**
 * Create a locale
 */
class Locale {
  _code: string;
  _messages: { [string]: string };
  _errors: { [string]: string };
  _nativeName: ?string;

  constructor(locale: LocaleConfiguration) {
    this._code = locale.code;

    this._messages = this.fixPlaceHoldersInObject(locale.messages);
    this._errors = this.fixPlaceHoldersInObject(locale.errors);

    this._nativeName = locale.nativeName;

    formatMessage.setup({
      missingTranslation: "ignore",
    });
  }

  /**
   * Retrieve locale code
   */
  get code(): string {
    return this._code;
  }

  /**
   * Retrieve nativeName
   */
  get nativeName(): string {
    if (this._nativeName) {
      return this._nativeName;
    }

    const aLocale = this.code.split("-");

    if (aLocale.length > 0) {
      const language = languages.lang[aLocale[0]];

      if (language) {
        return (
          language[1] + (aLocale[1] ? ` (${aLocale[1].toUpperCase()})` : "")
        );
      }
    }

    throw new IllegalStateException(
      `Can not find language name based on locale: ${this.code}`,
    );
  }

  /**
   * Exported messages from Be Informed have placeholders in the syntax ${placeholder_name}
   * where the ICU messageformat module uses {placeholder_name}.
   * This method converts the ${} to {}
   */
  fixPlaceHolders(message: ?string): null | string {
    if (typeof message === "string") {
      return message
        .replace(/'\$\{(?<key>.*?)}'/g, "''{$<key>}''")
        .replace(/\$\{(?<key>.*?)}/g, "{$<key>}");
    }

    return null;
  }

  /**
   * Exported messages from Be Informed have placeholders in the syntax ${placeholder_name}
   * where the ICU messageformat module uses {placeholder_name}.
   * This method converts the ${} to {}
   */
  fixPlaceHoldersInObject(messages: Object = {}): Object {
    return JSON.parse(
      JSON.stringify(messages)
        // $FlowIssue
        .replaceAll(/'\$\{(?<key>.*?)}'/g, "''{$<key>}''")
        // $FlowIssue
        .replaceAll(/\$\{(?<key>.*?)}/g, "{$<key>}"),
    );
  }

  /**
   * Retrieve message by id
   */
  getBaseMessage(id: string, defaultMessage?: string): string {
    if (!id && !defaultMessage) {
      return "[message: id or defaultMessage missing]";
    }

    const messageFromLocale = this._messages[id] || this._errors[id];

    if (
      (typeof process === "undefined" ||
        process.env.NODE_ENV !== "production") &&
      !messageFromLocale &&
      id &&
      getSetting("DEBUG_I18N_MESSAGE_NOT_FOUND")
    ) {
      // eslint-disable-next-line no-console
      console.debug(
        `Message with id ${id} not found for locale ${this.code} in layout translations`,
      );
    }
    return messageFromLocale || this.fixPlaceHolders(defaultMessage) || id;
  }

  /**
   * Format message using formatMessage library
   */
  formatMessage(message: string, parameters?: MessageParameters): string {
    if (parameters) {
      return formatMessage(message, parameters);
    }

    return message;
  }

  /**
   * Retrieve a messages with replaced parameters
   */
  getMessage(
    id: string,
    defaultMessage?: string,
    parameters?: MessageParameters,
  ): string {
    const message = this.getBaseMessage(id, defaultMessage);
    return this.formatMessage(message, parameters);
  }

  /**
   * Add or change messages for this locale. It is not possible to remove messages.
   */
  update(messages: Object = {}, errors: Object = {}) {
    this._messages = {
      ...this._messages,
      ...this.fixPlaceHoldersInObject(messages),
    };

    this._errors = {
      ...this._errors,
      ...this.fixPlaceHoldersInObject(errors),
    };
  }
}

export default Locale;
